import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Users, MapPin, Layers, FileText, ClipboardList, BarChart3,
  ChevronRight
} from 'lucide-react';
import { BACKEND_URL } from '../lib/config';

function StatCard({ icon: Icon, label, value, color = 'text-primary', sub }) {
  return (
    <Card className="bg-[#111827] border-border/60 card-hover">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground mb-1">{label}</p>
            <p className={`text-3xl font-bold ${color}`}>{value ?? '–'}</p>
            {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
          </div>
          <div className="w-10 h-10 bg-secondary/70 rounded-xl flex items-center justify-center">
            <Icon size={18} className={color} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/stats`);
      setStats(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Admin Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Welcome back, {user?.name}</p>
          </div>
          <Badge variant="outline" className="border-primary/30 text-primary">
            Super Admin
          </Badge>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Active Coaches" value={loading ? '...' : stats?.active_coaches} sub={`${stats?.total_coaches ?? ''} total`} />
          <StatCard icon={ClipboardList} label="Total Evaluations" value={loading ? '...' : stats?.total_evaluations} color="text-[#F7941D]" />
          <StatCard icon={MapPin} label="Locations" value={loading ? '...' : stats?.total_locations} />
          <StatCard icon={Layers} label="Batches" value={loading ? '...' : stats?.total_batches} />
          <StatCard icon={FileText} label="Templates" value={loading ? '...' : stats?.total_templates} />
          <StatCard icon={BarChart3} label="PDFs Generated" value={loading ? '...' : stats?.pdfs_generated} color="text-green-400" />
        </div>

        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Quick Actions</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Manage Coaches', icon: Users, path: '/admin/coaches', color: 'from-blue-900/40 to-blue-900/10' },
              { label: 'Locations & Batches', icon: MapPin, path: '/admin/locations', color: 'from-orange-900/40 to-orange-900/10' },
              { label: 'Templates', icon: FileText, path: '/admin/templates', color: 'from-purple-900/40 to-purple-900/10' },
              { label: 'All Evaluations', icon: ClipboardList, path: '/admin/evaluations', color: 'from-green-900/40 to-green-900/10' },
            ].map(({ label, icon: Icon, path, color }) => (
              <Link key={path} to={path}
                className={`flex items-center gap-3 p-4 rounded-xl bg-gradient-to-br ${color} border border-border/40 hover:border-primary/30 transition-colors`}
              >
                <Icon size={18} className="text-primary" />
                <span className="text-sm font-medium">{label}</span>
              </Link>
            ))}
          </div>
        </div>

        <Card className="bg-[#111827] border-border/60">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Recent Evaluations</CardTitle>
              <Link to="/admin/evaluations">
                <Button variant="ghost" size="sm" className="text-xs text-primary">View all <ChevronRight size={14} /></Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => <div key={`skeleton-${i}`} className="h-10 bg-secondary/50 rounded animate-pulse" />)}
              </div>
            ) : stats?.recent_evaluations?.length ? (
              <div className="space-y-2">
                {stats.recent_evaluations.map(ev => (
                  <Link key={ev.id} to={`/admin/evaluations/${ev.id}`}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/50 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium">{ev.cycle_name}</p>
                      <p className="text-xs text-muted-foreground">{ev.batch_name} · {ev.location_name} · {ev.coach_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{ev.player_count} players</Badge>
                      <ChevronRight size={14} className="text-muted-foreground" />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-6">No evaluations yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
